import { Link } from "react-router-dom";
import CountdownTimer from "./CountdownTimer";
import { fileUrl } from "@/lib/api";
import { Heart, Storefront, Lightning, Star, MapPin, ShieldCheck } from "@phosphor-icons/react";

const fallback = "https://images.unsplash.com/photo-1611095973362-88e8e2557e58?w=800&q=80";
const imgSrc = (p) => (!p ? fallback : p.startsWith("http") ? p : fileUrl(p));

/**
 * Faz 5A — Premium marketplace card.
 * - rounded-2xl, larger 5:4 image, premium-border for subtle definition
 * - SINGLE badge policy: highest-priority doping wins, avoid rozet spam
 * - Bottom row: price (dominant) + secondary "Hemen al" line + timer + city + bid count
 * - Bigger favorite button, subtle hover lift
 */

function StatusBadge({ auction }) {
  const closed = auction.status !== "active";
  const openEnded = auction.listing_type === "open_ended";
  const urgent = auction.doping_type === "urgent_badge";
  const premium = auction.doping_type === "homepage_vitrin";
  const catVitrin = auction.doping_type === "kategori_vitrin";
  const topRow = auction.doping_type === "top_row";
  const hot = auction.is_hot_auction && !closed;

  if (urgent) {
    return (
      <span className="inline-flex items-center gap-1 bg-[#B54037] text-white text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full">
        <Lightning size={10} weight="fill" /> Acil
      </span>
    );
  }
  if (premium) {
    return (
      <span className="inline-flex items-center gap-1 bg-slate-900 text-white text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full">
        <Star size={10} weight="fill" className="text-amber-400" /> Öne çıkan
      </span>
    );
  }
  if (hot) {
    return (
      <span
        data-testid={`hot-badge-${auction.id}`}
        className="hot-badge inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full"
      >
        Kızışan
      </span>
    );
  }
  if (catVitrin) {
    return (
      <span
        data-testid={`vitrin-badge-${auction.id}`}
        className="inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-slate-800 text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full premium-border"
      >
        <Star size={10} weight="fill" className="text-amber-500" /> Vitrin
      </span>
    );
  }
  if (topRow) {
    return (
      <span
        data-testid={`toprow-badge-${auction.id}`}
        className="inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-slate-700 text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full premium-border"
      >
        ▲ Üst sıra
      </span>
    );
  }
  if (openEnded) {
    return (
      <span className="inline-flex items-center bg-white/95 backdrop-blur-sm text-slate-700 text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full premium-border">
        Süresiz
      </span>
    );
  }
  if (!closed) {
    return (
      <span className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm text-[#B54037] text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full premium-border">
        <span className="live-dot" /> Canlı
      </span>
    );
  }
  return null;
}

export default function AuctionCard({ auction, onFav, isFav }) {
  const closed = auction.status !== "active";
  const openEnded = auction.listing_type === "open_ended";
  const bold = auction.doping_type === "bold_title";
  const primaryPrice = openEnded
    ? auction.buy_now_price || auction.current_price
    : auction.current_price;

  return (
    <Link
      to={`/mezat/${auction.id}`}
      data-testid={`auction-card-${auction.id}`}
      className="group block bg-white rounded-2xl overflow-hidden premium-border soft-shadow hover:soft-shadow-md hover:-translate-y-1 transition-all duration-500"
    >
      <div className="relative aspect-[5/4] bg-slate-50 overflow-hidden">
        <img
          src={imgSrc(auction.images?.[0])}
          alt={auction.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms] ease-out"
          onError={(e) => { e.target.src = fallback; }}
        />

        {/* Single badge — top-left */}
        <div className="absolute top-3 left-3">
          <StatusBadge auction={auction} />
        </div>

        {/* Favorite button — top-right, larger tap area */}
        {onFav && (
          <button
            data-testid={`fav-btn-${auction.id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFav(auction.id); }}
            className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm p-2.5 rounded-full hover:scale-110 active:scale-95 transition-transform premium-border"
            aria-label="Favoriye ekle"
          >
            <Heart size={16} weight={isFav ? "fill" : "regular"} color={isFav ? "#B54037" : "#334155"} />
          </button>
        )}

        {/* Store trust badge — bottom-left, subtle */}
        {auction.store_slug && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-full premium-border">
            <Storefront size={11} weight="bold" className="text-slate-700" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-700">Mağaza</span>
          </div>
        )}
      </div>

      <div className="p-5 space-y-3">
        {/* Meta row: category + city */}
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] font-medium text-slate-400">
          <span className="truncate">{auction.category}</span>
          {auction.city && (
            <span className="flex items-center gap-1 normal-case tracking-normal text-slate-500 shrink-0 ml-2">
              <MapPin size={11} /> {auction.city}
            </span>
          )}
        </div>

        <h3
          className={`font-display leading-tight line-clamp-2 min-h-[2.6rem] tracking-tight ${
            bold ? "text-lg font-medium text-[#B54037]" : "text-lg font-medium text-slate-900"
          }`}
        >
          {auction.title}
        </h3>

        {/* Price row */}
        <div className="flex items-end justify-between pt-4 border-t divider">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-1">
              {openEnded ? "Hemen al" : "Mevcut teklif"}
            </p>
            <p
              className="font-mono-tight text-xl font-medium text-slate-900 truncate"
              data-testid={`card-price-${auction.id}`}
            >
              ₺{Number(primaryPrice).toLocaleString("tr-TR")}
            </p>
            {!openEnded && auction.buy_now_price && (
              <p className="text-[11px] text-slate-500 mt-0.5 font-mono-tight">
                Hemen al · ₺{Number(auction.buy_now_price).toLocaleString("tr-TR")}
              </p>
            )}
          </div>
          <div className="text-right shrink-0 ml-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-1">
              {closed ? "Durum" : openEnded ? "Süre" : "Kalan"}
            </p>
            {openEnded ? (
              <span className="font-mono-tight text-sm font-medium text-slate-500">Süresiz</span>
            ) : (
              <CountdownTimer endTime={auction.end_time} size="sm" />
            )}
          </div>
        </div>

        {/* Foot row: bid count + condition/verified */}
        <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1">
          <span>{auction.bid_count} teklif</span>
          <div className="flex items-center gap-2">
            {auction.condition === "new" && (
              <span className="uppercase tracking-[0.2em] text-[9px] font-medium text-emerald-700">
                Sıfır
              </span>
            )}
            {auction.seller_is_verified && (
              <ShieldCheck size={12} weight="fill" className="text-emerald-600" title="Doğrulanmış satıcı" />
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
